import React from "react";
import { Box } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SearchIcon from "@mui/icons-material/Search";
import MicIcon from "@mui/icons-material/Mic";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import AnimationIcon from "@mui/icons-material/Animation";
import {
  RANGES,
  ThemeConfig,
  ThemeLayer,
} from "../../../../service/BuyerConfiguration/BuyerAppDashboard/homeThemesService";
import { DEFAULT_TAB_SHAPE, TabShape, shapePath } from "../TabShapeEditor";
import { ParticleCanvas, ParticleCanvasHandle } from "./ParticleCanvas";
import { EdgeShape, Garland, edgeHeight, edgeRadius } from "./DashDecor";
import { effectiveColors } from "./themeUtils";

/** Design width of the dash; smaller previews are scaled down from this. */
export const BASE_W = 360;
const PAGE_BG = "#FFFFFF";

const STATUS_H = 26;
const LOCATION_H = 46;
const GREETING_H = 28;
const SEARCH_H = 54;
const TAB_OVERLAP = 10;
const CARD_W = (BASE_W - 20 + TAB_OVERLAP * 3) / 4;
const CARD_H = CARD_W * 0.9;
const TABS_H = CARD_H + 16;

const CARDS = [
  { title: "Craves", icon: "🍔" },
  { title: "Delicious Biryani", icon: "🍛" },
  { title: "Cool & Beverages", icon: "🥤" },
  { title: "Desserts & Sweets", icon: "🍰" },
];

/** Total dash height at BASE_W for a config. */
export const dashHeight = (cfg: ThemeConfig) =>
  STATUS_H + LOCATION_H + (cfg.greeting.text || cfg.greeting.textTa ? GREETING_H : 0) + SEARCH_H + TABS_H + edgeHeight(cfg.edge);

const KEYFRAMES = `
@keyframes tsx-float { 0% { transform: translateY(0) } 50% { transform: translateY(-6px) } 100% { transform: translateY(0) } }
@keyframes tsx-sway { 0% { transform: rotate(-8deg) } 100% { transform: rotate(8deg) } }
@keyframes tsx-spin { 0% { transform: rotate(0deg) } 100% { transform: rotate(360deg) } }
@keyframes tsx-pulse { 0% { transform: scale(1) } 100% { transform: scale(1.14) } }
@keyframes tsx-garland-sway { 0% { transform: rotate(-5deg) } 100% { transform: rotate(5deg) } }
@keyframes tsx-twinkle { 0% { opacity: 1 } 100% { opacity: 0.35 } }
@keyframes tsx-shimmer { 0% { background-position: 200% 0 } 100% { background-position: -200% 0 } }
@keyframes tsx-glow { 0% { text-shadow: 0 0 2px var(--tsx-glow) } 100% { text-shadow: 0 0 10px var(--tsx-glow), 0 0 18px var(--tsx-glow) } }
@keyframes tsx-bounce { 0%, 100% { transform: translateY(0) } 30% { transform: translateY(-5px) } 50% { transform: translateY(0) } 65% { transform: translateY(-2px) } }
@keyframes tsx-rotate-y { 0% { transform: rotateY(0deg) } 100% { transform: rotateY(360deg) } }
`;

export const usePrefersReducedMotion = () => {
  const query = "(prefers-reduced-motion: reduce)";
  const [reduced, setReduced] = React.useState(
    () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(query).matches,
  );
  React.useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia(query);
    const on = () => setReduced(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", on);
    else mq.addListener(on);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", on);
      else mq.removeListener(on);
    };
  }, []);
  return reduced;
};

const gradientAngle = { vertical: 180, horizontal: 90, diagonal: 135 } as const;

const daysUntil = (iso: string | null) => {
  if (!iso) return null;
  const target = new Date(`${iso.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = Math.round((target.getTime() - today.getTime()) / 86400000);
  return d > 0 ? d : null;
};

/* ------------------------------- layers ------------------------------- */

const motionAnimation = (l: ThemeLayer, idx: number, animate: boolean): { anim?: string; keyframes?: string } => {
  if (!animate || l.motion === "none") return {};
  const sp = Math.max(0.1, l.speed);
  switch (l.motion) {
    case "float":
      return { anim: `tsx-float ${3 / sp}s ease-in-out infinite` };
    case "sway":
      return { anim: `tsx-sway ${1.2 / sp}s ease-in-out infinite alternate` };
    case "spin":
      return { anim: `tsx-spin ${8 / sp}s linear infinite` };
    case "pulse":
      return { anim: `tsx-pulse ${0.8 / sp}s ease-in-out infinite alternate` };
    case "flyby": {
      // Fly across the whole dash; with "every", wait off-screen in between.
      const size = l.w * BASE_W;
      const from = -(l.x * BASE_W + size);
      const to = BASE_W - l.x * BASE_W + size;
      const flight = 4 / sp;
      const total = l.every > 0 ? Math.max(flight, l.every) : flight;
      const end = Math.min(100, (flight / total) * 100);
      const name = `tsx-fly-${idx}-${Math.round(from)}-${Math.round(total * 10)}`;
      const keyframes = `@keyframes ${name} { 0% { transform: translateX(${from}px) } ${end.toFixed(1)}% { transform: translateX(${to}px) } 100% { transform: translateX(${to}px) } }`;
      return { anim: `${name} ${total}s linear infinite`, keyframes };
    }
    default:
      return {};
  }
};

const LayerView: React.FC<{
  layer: ThemeLayer;
  idx: number;
  dashH: number;
  animate: boolean;
  tilt: { x: number; y: number };
  selected: boolean;
  draggable: boolean;
  onPointerDown?: (e: React.PointerEvent) => void;
}> = ({ layer, idx, dashH, animate, tilt, selected, draggable, onPointerDown }) => {
  const size = layer.w * BASE_W;
  const { anim, keyframes } = motionAnimation(layer, idx, animate);
  const tx = tilt.x * layer.depth * 10;
  const ty = tilt.y * layer.depth * 6;
  let inner: React.ReactNode;
  if (layer.type === "emoji") {
    inner = (
      <span style={{ fontSize: size * 0.85, lineHeight: 1, display: "block", whiteSpace: "nowrap" }}>
        {layer.src || "❔"}
      </span>
    );
  } else if (layer.type === "image") {
    inner = layer.src ? (
      <img src={layer.src} alt="" draggable={false} style={{ width: size, display: "block" }} />
    ) : (
      <Box sx={{ width: size, height: size * 0.6, border: "1px dashed rgba(255,255,255,0.8)", borderRadius: 1, color: "#fff", fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
        Image
      </Box>
    );
  } else {
    inner = (
      <Box
        sx={{
          width: size,
          height: size,
          border: "1.5px dashed rgba(255,255,255,0.9)",
          borderRadius: 1.5,
          bgcolor: "rgba(0,0,0,0.18)",
          color: "#fff",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          fontSize: Math.max(8, Math.min(12, size / 6)),
          fontWeight: 700,
          textAlign: "center",
          lineHeight: 1.1,
        }}
      >
        <AnimationIcon sx={{ fontSize: Math.max(12, size / 3) }} />
        Lottie
      </Box>
    );
  }
  return (
    <div
      onPointerDown={onPointerDown}
      style={{
        position: "absolute",
        left: layer.x * BASE_W + tx,
        top: layer.y * dashH + ty,
        transform: "translate(-50%, -50%)",
        opacity: layer.opacity,
        zIndex: layer.front ? 6 : 1,
        cursor: draggable ? "grab" : undefined,
        touchAction: draggable ? "none" : undefined,
        outline: selected ? "2px dashed #FFD54A" : undefined,
        outlineOffset: 2,
        pointerEvents: draggable ? "auto" : "none",
        userSelect: "none",
      }}
    >
      {keyframes && <style>{keyframes}</style>}
      <div style={{ animation: anim, transformOrigin: layer.motion === "sway" ? "50% 100%" : "50% 50%" }}>{inner}</div>
    </div>
  );
};

/* -------------------------------- tabs -------------------------------- */

const TabsRow: React.FC<{ shape: TabShape; c: ReturnType<typeof effectiveColors>; uid: string }> = ({ shape, c, uid }) => {
  const ring = 3;
  return (
    <Box sx={{ display: "flex", alignItems: "center", px: "10px", height: TABS_H, position: "relative", zIndex: 3 }}>
      {CARDS.map((card, i) => {
        const selected = i === 0;
        return (
          <Box
            key={card.title}
            sx={{
              position: "relative",
              width: CARD_W,
              height: CARD_H,
              flexShrink: 0,
              ml: i === 0 ? 0 : `${-TAB_OVERLAP}px`,
              zIndex: selected ? 10 : 4 - i,
            }}
          >
            <svg width={CARD_W} height={CARD_H} style={{ position: "absolute", inset: 0 }}>
              <defs>
                <linearGradient id={`${uid}-tg${i}`} x1="0.15" y1="0" x2="0.85" y2="1">
                  <stop offset="0" stopColor={c.tabFrom} />
                  <stop offset="1" stopColor={c.tabTo} />
                </linearGradient>
              </defs>
              <path d={shapePath(shape, CARD_W, CARD_H, ring)} fill="rgba(0,0,0,0.16)" transform="translate(1,2)" />
              {selected ? (
                <>
                  <path d={shapePath(shape, CARD_W, CARD_H, 0)} fill={c.tabSelectedRing} />
                  <path d={shapePath(shape, CARD_W, CARD_H, ring)} fill={c.tabSelectedFill} />
                </>
              ) : (
                <path d={shapePath(shape, CARD_W, CARD_H, ring)} fill={`url(#${uid}-tg${i})`} stroke="rgba(255,255,255,0.32)" />
              )}
            </svg>
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                pl: i === 0 ? "4px" : `${TAB_OVERLAP}px`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span style={{ fontSize: CARD_H * 0.34, lineHeight: 1.1 }}>{card.icon}</span>
              <Box
                sx={{
                  fontWeight: 800,
                  fontSize: selected ? 11.5 : 9,
                  lineHeight: 1.15,
                  textAlign: "center",
                  color: selected ? c.tabSelectedText : c.tabText,
                  px: "2px",
                  mt: "2px",
                }}
              >
                {card.title}
              </Box>
              <Box
                sx={{
                  width: 20,
                  height: 3,
                  borderRadius: 2,
                  mt: "2px",
                  bgcolor: selected ? c.tabUnderlineSelected : c.tabUnderline,
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

/* ------------------------------- preview ------------------------------- */

export type ThemePreviewProps = {
  config: ThemeConfig;
  /** Rendered width in px (the design is 360 wide and scaled). */
  width?: number;
  night?: boolean;
  lang?: "en" | "ta";
  /** No animation at all (thumbnails). */
  still?: boolean;
  tabShape?: TabShape | null;
  /** Wrap in a phone frame with a hint of the page below the dash. */
  phone?: boolean;
  selectedLayerId?: string | null;
  onLayerSelect?: (id: string | null) => void;
  /** When set, layers can be dragged on the preview. */
  onLayerMove?: (id: string, x: number, y: number) => void;
};

let uidCounter = 0;

export const ThemePreview: React.FC<ThemePreviewProps> = ({
  config,
  width = BASE_W,
  night = false,
  lang = "en",
  still = false,
  tabShape,
  phone = false,
  selectedLayerId = null,
  onLayerSelect,
  onLayerMove,
}) => {
  const reduced = usePrefersReducedMotion();
  const animate = !still && !reduced;
  const uid = React.useMemo(() => `tsx${++uidCounter}`, []);
  const c = effectiveColors(config, night);
  const H = dashHeight(config);
  const scale = width / BASE_W;
  const dashRef = React.useRef<HTMLDivElement | null>(null);
  const canvasRef = React.useRef<ParticleCanvasHandle | null>(null);
  const drag = React.useRef<{ id: string; moved: boolean } | null>(null);
  const [tilt, setTilt] = React.useState({ x: 0, y: 0 });
  const tiltOn = animate && config.interaction.tilt && !!onLayerMove;
  const shape = tabShape ?? DEFAULT_TAB_SHAPE;

  const angle = gradientAngle[c.dashDirection] ?? 180;
  const stops = c.dashVia ? `${c.dashFrom}, ${c.dashVia}, ${c.dashTo}` : `${c.dashFrom}, ${c.dashTo}`;
  const radius = edgeRadius(config.edge);

  const toLocal = (e: React.PointerEvent | React.MouseEvent) => {
    const rect = dashRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      fx: (e.clientX - rect.left) / rect.width,
      fy: (e.clientY - rect.top) / rect.height,
    };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const p = toLocal(e);
    if (!p) return;
    if (drag.current && onLayerMove) {
      drag.current.moved = true;
      const clamp = (v: number, r: { min: number; max: number }) =>
        Math.round(Math.min(r.max, Math.max(r.min, v)) * 100) / 100;
      onLayerMove(drag.current.id, clamp(p.fx, RANGES.layerX), clamp(p.fy, RANGES.layerY));
      return;
    }
    if (tiltOn) setTilt({ x: (p.fx - 0.5) * 2, y: (p.fy - 0.5) * 2 });
  };

  const suppressClick = React.useRef(false);
  const endDrag = () => {
    if (drag.current?.moved) suppressClick.current = true;
    drag.current = null;
  };

  const onClick = (e: React.MouseEvent) => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    if (!config.interaction.tapBurst || !animate) return;
    const p = toLocal(e);
    if (!p) return;
    canvasRef.current?.burst(p.fx * BASE_W, p.fy * H);
  };

  const days = daysUntil(config.greeting.countdownTo);
  const greetingText = lang === "ta" && config.greeting.textTa ? config.greeting.textTa : config.greeting.text || config.greeting.textTa;
  const g = config.greeting;
  const greetingStyle: React.CSSProperties = { color: g.color, display: "inline-block" };
  if (animate && g.effect === "shimmer") {
    Object.assign(greetingStyle, {
      backgroundImage: `linear-gradient(90deg, ${g.color} 35%, #FFFFFF 50%, ${g.color} 65%)`,
      backgroundSize: "200% 100%",
      WebkitBackgroundClip: "text",
      backgroundClip: "text",
      color: "transparent",
      animation: "tsx-shimmer 2.8s linear infinite",
    });
  } else if (animate && g.effect === "glow") {
    Object.assign(greetingStyle, { ["--tsx-glow" as any]: g.color, animation: "tsx-glow 1.6s ease-in-out infinite alternate" });
  } else if (animate && g.effect === "bounce") {
    greetingStyle.animation = "tsx-bounce 1.8s ease-in-out infinite";
  } else if (g.effect === "glow") {
    greetingStyle.textShadow = `0 0 8px ${g.color}`;
  }

  const statusColor = c.statusBar === "dark" ? "#111111" : "#FFFFFF";
  const m = config.model3d;
  const bg = config.background;

  const dash = (
    <div
      ref={dashRef}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerLeave={() => {
        endDrag();
        if (tiltOn) setTilt({ x: 0, y: 0 });
      }}
      onClick={onClick}
      style={{
        position: "relative",
        width: BASE_W,
        height: H,
        overflow: "hidden",
        background: `linear-gradient(${angle}deg, ${stops})`,
        borderBottomLeftRadius: radius,
        borderBottomRightRadius: radius,
        fontFamily: "Poppins, Roboto, sans-serif",
        cursor: config.interaction.tapBurst && animate ? "pointer" : undefined,
        userSelect: "none",
      }}
    >
      <style>{KEYFRAMES}</style>
      {bg.imageUrl && (
        <div
          style={{
            position: "absolute",
            inset: -12,
            backgroundImage: `url("${bg.imageUrl}")`,
            backgroundRepeat: bg.repeat ? "repeat" : "no-repeat",
            backgroundSize: bg.repeat ? "auto" : "cover",
            backgroundPosition: "center",
            opacity: bg.opacity,
            transform: `translate(${tilt.x * bg.parallax * 10}px, ${tilt.y * bg.parallax * 6}px)`,
            pointerEvents: "none",
          }}
        />
      )}

      {/* back layers */}
      {config.layers.map((l, i) =>
        l.front ? null : (
          <LayerView
            key={l.id + i}
            layer={l}
            idx={i}
            dashH={H}
            animate={animate}
            tilt={tilt}
            selected={selectedLayerId === l.id}
            draggable={!!onLayerMove}
            onPointerDown={
              onLayerMove
                ? (e) => {
                    e.stopPropagation();
                    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
                    drag.current = { id: l.id, moved: false };
                    onLayerSelect?.(l.id);
                  }
                : undefined
            }
          />
        ),
      )}

      {m.url && (
        <div
          style={{
            position: "absolute",
            left: m.x * BASE_W,
            top: m.y * H,
            width: m.size * BASE_W,
            height: m.size * BASE_W,
            transform: "translate(-50%, -50%)",
            zIndex: 4,
            perspective: 300,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              border: "1.5px dashed rgba(255,255,255,0.9)",
              borderRadius: 8,
              background: "rgba(0,0,0,0.2)",
              color: "#fff",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
              fontWeight: 700,
              animation: animate && m.autoRotate ? `tsx-rotate-y ${6 / m.rotateSpeed}s linear infinite` : undefined,
            }}
          >
            <ViewInArIcon sx={{ fontSize: Math.max(16, m.size * BASE_W * 0.45) }} />
            3D model
          </div>
        </div>
      )}

      {/* content */}
      <div style={{ position: "relative", zIndex: 3 }}>
        <div
          style={{
            height: STATUS_H,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 16px",
            color: statusColor,
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          <span>9:41</span>
          <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <span style={{ width: 14, height: 8, borderRadius: 1, background: statusColor, opacity: 0.9 }} />
            <span style={{ width: 20, height: 9, borderRadius: 2, border: `1.5px solid ${statusColor}`, position: "relative" }}>
              <span style={{ position: "absolute", left: 1, top: 1, bottom: 1, width: "70%", background: statusColor }} />
            </span>
          </span>
        </div>
        <div style={{ height: LOCATION_H, display: "flex", alignItems: "center", padding: "0 12px", color: c.onDash }}>
          <LocationOnIcon sx={{ fontSize: 24, color: c.onDash }} />
          <div style={{ marginLeft: 4, flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 15, lineHeight: 1.2 }}>Home ▾</div>
            <div style={{ fontSize: 11.5, opacity: 0.92, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              12, North Masi Street, Madurai
            </div>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              border: `1.5px solid ${c.onDash}`,
              borderRadius: 16,
              padding: "3px 9px",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 15, color: c.onDash }} />
            ₹120
          </div>
        </div>
        {(g.text || g.textTa) && (
          <div
            style={{
              height: GREETING_H,
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "0 14px",
              fontWeight: 800,
              fontSize: lang === "ta" ? 13 : 15,
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            <span style={greetingStyle}>{greetingText}</span>
            {days !== null && (
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: c.onDash,
                  border: `1px solid ${c.onDash}`,
                  borderRadius: 10,
                  padding: "1px 7px",
                  opacity: 0.9,
                }}
              >
                in {days} {days === 1 ? "day" : "days"}
              </span>
            )}
          </div>
        )}
        <div style={{ height: SEARCH_H, padding: "5px 12px 7px" }}>
          <div
            style={{
              height: "100%",
              borderRadius: 12,
              background: c.searchBg,
              display: "flex",
              alignItems: "center",
              padding: "0 12px",
              gap: 8,
              boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
            }}
          >
            <SearchIcon sx={{ color: c.searchIcon, fontSize: 21 }} />
            <span style={{ flex: 1, color: c.searchText, fontSize: 13.5 }}>Search “Biryani”</span>
            <span style={{ width: 1, height: 20, background: "rgba(0,0,0,0.12)" }} />
            <MicIcon sx={{ color: c.micColor, fontSize: 21 }} />
          </div>
        </div>
        <TabsRow shape={shape} c={c} uid={uid} />
      </div>

      {config.particles.preset !== "none" || config.interaction.tapBurst ? (
        <div style={{ position: "absolute", inset: 0, zIndex: 5, pointerEvents: "none" }}>
          <ParticleCanvas
            ref={canvasRef}
            width={BASE_W}
            height={H}
            particles={config.particles}
            burst={config.interaction.burst}
            still={!animate}
          />
        </div>
      ) : null}

      {/* front layers */}
      {config.layers.map((l, i) =>
        l.front ? (
          <LayerView
            key={l.id + i}
            layer={l}
            idx={i}
            dashH={H}
            animate={animate}
            tilt={tilt}
            selected={selectedLayerId === l.id}
            draggable={!!onLayerMove}
            onPointerDown={
              onLayerMove
                ? (e) => {
                    e.stopPropagation();
                    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
                    drag.current = { id: l.id, moved: false };
                    onLayerSelect?.(l.id);
                  }
                : undefined
            }
          />
        ) : null,
      )}

      <div style={{ position: "absolute", left: 0, right: 0, top: 0, zIndex: 7, pointerEvents: "none" }}>
        <Garland garland={config.garland} width={BASE_W} animate={animate} />
      </div>
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: edgeHeight(config.edge), zIndex: 8, pointerEvents: "none" }}>
        <EdgeShape edge={config.edge} width={BASE_W} pageColor={PAGE_BG} />
      </div>
    </div>
  );

  if (!phone) {
    return (
      <Box sx={{ width, height: H * scale, overflow: "hidden", borderRadius: 2, bgcolor: PAGE_BG, flexShrink: 0 }}>
        <Box sx={{ width: BASE_W, transform: `scale(${scale})`, transformOrigin: "0 0" }}>{dash}</Box>
      </Box>
    );
  }

  // Phone frame: the dash plus a hint of the page under it.
  const PHONE_H = 640;
  return (
    <Box
      sx={{
        width: width + 20,
        p: "10px",
        borderRadius: "36px",
        bgcolor: "#111",
        boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
        flexShrink: 0,
      }}
    >
      <Box sx={{ width, height: PHONE_H * scale, overflow: "hidden", borderRadius: "28px", bgcolor: PAGE_BG }}>
        <Box sx={{ width: BASE_W, height: PHONE_H, transform: `scale(${scale})`, transformOrigin: "0 0" }}>
          {dash}
          <Box sx={{ px: "14px", pt: "12px" }}>
            <Box sx={{ height: 14, width: 150, borderRadius: 1, bgcolor: "#ECEFF1", mb: 1.5 }} />
            <Box sx={{ display: "flex", gap: "10px", mb: 2 }}>
              {[0, 1, 2, 3].map((i) => (
                <Box key={i} sx={{ flex: 1 }}>
                  <Box sx={{ width: "100%", aspectRatio: "1", borderRadius: "50%", bgcolor: "#F1F3F4" }} />
                  <Box sx={{ height: 8, mt: 0.75, borderRadius: 1, bgcolor: "#F1F3F4" }} />
                </Box>
              ))}
            </Box>
            {[0, 1].map((i) => (
              <Box key={i} sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
                <Box sx={{ width: 96, height: 80, borderRadius: 2, bgcolor: "#F1F3F4" }} />
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ height: 12, width: "70%", borderRadius: 1, bgcolor: "#ECEFF1", mb: 1 }} />
                  <Box sx={{ height: 9, width: "50%", borderRadius: 1, bgcolor: "#F1F3F4", mb: 1 }} />
                  <Box sx={{ height: 9, width: "35%", borderRadius: 1, bgcolor: "#F1F3F4" }} />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ThemePreview;
